import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { RestApiService } from '@draeger-academy/common';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { environment } from '../../../../../../../src/environments/environment';
import {
    CommunicationConsentCheckbox,
    ConsentData,
    CustomErrorResponse,
    DependentFieldFilters,
    Field,
    FormBuilderService,
    FormsUtils,
    FormUtilsService,
    HubspotFormData
} from '../../../form-builder';
import { PrepopulationHandlingService } from './prepopulation-handling.service';

@Injectable()
export class HubspotService {
    private _$currentFormData: { [key: string]: BehaviorSubject<HubspotFormData> } = {};
    private _$hubspotIdToHash: Map<string, BehaviorSubject<any>> = new Map();
    private _$currentFormDataFlattened: { [key: string]: BehaviorSubject<Field[]> } = {};
    private _hubPortalId = '3926732';
    private _hsFormId: string;
    private _ip: string;
    private _prepopData = [];

    constructor(
        private _restApi: RestApiService,
        private _formUtilsService: FormUtilsService,
        private _httpClient: HttpClient,
        private _formBuilderService: FormBuilderService,
        private _prepopulationHandlingService: PrepopulationHandlingService
    ) {}

    public currentFormData$(guid: string): Observable<HubspotFormData> {
        if (!this._$currentFormData[guid]) {
            this._$currentFormData[guid] = new BehaviorSubject(null);
        }
        return this._$currentFormData[guid].asObservable();
    }

    public currentFormDataFlattened$(guid: string): Observable<any> {
        if (!this._$currentFormDataFlattened[guid]) {
            this._$currentFormDataFlattened[guid] = new BehaviorSubject(null);
        }
        return this._$currentFormDataFlattened[guid].asObservable();
    }

    public currentFormMetadata$(guid: string): Observable<any> {
        return of(this._$currentFormData[guid].getValue().metaData);
    }

    public getDataByGuid(guid: string, hash: string): Promise<HubspotFormData | CustomErrorResponse> {
        //const _url = `${this._environmentService['apiEndpoints'].proxy.v1.hubspot.v1.hubspot}/` + guid;
        const _url = `${environment.APIEndpoint}api/v1/HubSpot/` + guid;
        // this._setMapEntry(guid);
        return this._restApi.requestGET<HubspotFormData>(_url).then((res: any) => {
            if (!res.status || res.status !== 'error') {
                if (!this._$currentFormData[hash]) {
                    this._$currentFormData[hash] = new BehaviorSubject(null);
                }

                this._$currentFormData[hash].next(res);

                if (!this._$currentFormDataFlattened[hash]) {
                    this._$currentFormDataFlattened[hash] = new BehaviorSubject(null);
                }
                this._$currentFormDataFlattened[hash].next(this._getFlattendHubspotData(res));
            }
            return res;
        });
    }

    public postDataToHubspot(guid: string, formData: UntypedFormGroup, hash: string, webinarId: string = null): Observable<any> {
        this._hsFormId = guid;
        return this._sendData(formData, guid, hash, webinarId).pipe((res) => {
            return res;
        });
    }

    public getReactiveFormFromSchema(guid: string, hash: string, addHsContext = true, params = {}, setPrepopData: boolean): UntypedFormGroup {
        const formControls = {};
        this.currentFormDataFlattened$(hash).subscribe((data) => {
            data.forEach((field: any) => {
                const _value = this._getValueByField(field, setPrepopData);
                if (this._formBuilderService.getFormControlByFieldType(field, _value)) {
                    formControls[field.name] = this._formBuilderService.getFormControlByFieldType(field, _value);
                }
                if (params[field.name]) {
                    formControls[field.name].value = params[field.name];
                }
                if (field.dependentFieldFilters.length > 0) {
                    field.dependentFieldFilters.forEach((dField: DependentFieldFilters) => {
                        if (this._formBuilderService.getFormControlByFieldType(dField.dependentFormField)) {
                            formControls[dField.dependentFormField.name] = this._formBuilderService.getFormControlByFieldType(
                                dField.dependentFormField,
                                this._getValueByField(dField.dependentFormField, setPrepopData)
                            );
                        }
                    });
                }
            });
        });

        return new UntypedFormGroup(formControls);
    }

    public removeFormByGuid(guid: string) {
        if (this._$currentFormData[guid]) {
            delete this._$currentFormData[guid];
            this._$hubspotIdToHash.delete(guid);
        }
    }

    public setPrepopulationData(usePopulation: boolean, hubspotId: string): any {
        if (!usePopulation) {
            return new Promise<void>((resolve) => resolve());
        }
        return new Promise<void>((resolve) => {
            this._prepopulationHandlingService.lsData$.pipe().subscribe((data) => {
                if (data !== null) {
                    this._prepopData = data;
                    resolve();
                }
            });
        });
    }

    public setMapEntry(hubspotId: string, hash: string) {
        if (!this._$hubspotIdToHash.has(hash)) {
            this._$hubspotIdToHash.set(hash, new BehaviorSubject(hubspotId));
        }
    }

    public getMapEntryByHash(hash: string): string {
        const invertedMap = new Map([...this._$hubspotIdToHash.entries()].map(([key, value]) => [value.getValue(), key]));
        return invertedMap.get(hash);
    }

    public getMapEntryByHubspotId(hubspotId: string): string {
        const invertedMap = new Map([...this._$hubspotIdToHash.entries()].map(([key, value]) => [value.getValue(), key]));
        return this._$hubspotIdToHash.get(hubspotId).getValue();
    }

    private _sendData(formGroup: any, guid: string, hash: string, webinarId: string = null): Observable<any> {
        const setIp = this._formUtilsService.getIp().then((ip) => (this._ip = ip));
        return from(setIp).pipe(
            mergeMap((ip) => {
                const _url = 'https://api.hsforms.com/submissions/v3/integration/submit/' + this._hubPortalId + '/' + this._hsFormId;
                const formData = this._prepareJSONData(formGroup, guid, hash, webinarId);
                const httpOptions = {
                    headers: new HttpHeaders({
                        'Content-Type': 'application/json'
                    })
                };

                return this._httpClient.post<any>(_url, JSON.stringify(formData), httpOptions).pipe(
                    map((res) => {
                        this._prepopulationHandlingService.setLocalStorage(formData);
                        return res;
                    }),
                    catchError((err) => {
                        return of(err);
                    })
                );
            })
        );
    }

    private _prepareJSONData(formGroup: any, guid: string, hash: string, webinarId: string = null) {
        const data = {
            submittedAt: new Date().getTime(),
            fields: [],
            legalConsentOptions: null,
            context: null,
            skipValidation: true
        };
        data.legalConsentOptions = this._getLegalConsentData(formGroup, guid, hash);
        data.context = this._getContextData(webinarId);
        Object.keys(formGroup.controls).forEach((key) => {
            if (key.indexOf('LEGAL_CONSENT.') < 0) {
                const _currentField = this._$currentFormDataFlattened[hash].getValue().filter((f) => {
                    return f.name === key;
                })[0];
                if (FormsUtils.isCheckboxes(_currentField)) {
                    formGroup
                        .get(key)
                        .value.split(',')
                        .forEach((val: string) => {
                            data.fields.push({ name: key, value: val || '' });
                        });
                } else {
                    data.fields.push({ name: key, value: formGroup.get(key).value || '' });
                }
            }
        });
        return data;
    }

    private _getLegalConsentData(formData: UntypedFormGroup, guid: string, hash: string) {
        const _legalConsentData = this._$currentFormData[hash].getValue().metaData.filter((data) => data.name === 'legalConsentOptions')[0];
        const _legalConsentJson: ConsentData = JSON.parse(_legalConsentData.value);
        const _isLegitimateInterest = _legalConsentJson.isLegitimateInterest;

        if (_isLegitimateInterest) {
            return {
                legitimateInterest: {
                    value: true,
                    subscriptionTypeId: _legalConsentJson.communicationConsentCheckboxes[0].communicationTypeId,
                    legalBasis: _legalConsentJson.legitimateInterestLegalBasis,
                    text: _legalConsentJson.communicationConsentCheckboxes[0].label
                }
            };
        } else {
            const _consentToProcess =
                _legalConsentJson.processingConsentType === 'IMPLICIT'
                    ? true
                    : formData.controls['LEGAL_CONSENT.processing']
                    ? formData.controls['LEGAL_CONSENT.processing'].value
                    : false;
            return {
                consent: {
                    consentToProcess: _consentToProcess,
                    text: _legalConsentJson.processingConsentCheckboxLabel,
                    communications: this._getConsentCommunicationCheckboxValues(_legalConsentJson.communicationConsentCheckboxes, formData)
                }
            };
        }
    }

    private _getConsentCommunicationCheckboxValues(communicationConsentCheckboxes: CommunicationConsentCheckbox[], formData: UntypedFormGroup) {
        const _communications = [];
        if (formData) {
            communicationConsentCheckboxes.forEach((communicationConsentCheckbox) => {
                const _control = formData.controls['LEGAL_CONSENT.subscription_type_' + communicationConsentCheckbox.communicationTypeId];
                _communications.push({
                    value: _control ? _control.value : false,
                    subscriptionTypeId: communicationConsentCheckbox.communicationTypeId,
                    text: communicationConsentCheckbox.label
                });
            });
        }
        return _communications;
    }

    private _getFlattendHubspotData(data: HubspotFormData) {
        const _flattened = [];
        data.formFieldGroups.forEach((formFieldGroup) => {
            formFieldGroup.fields.forEach((field: Field) => {
                _flattened.push(field);
            });
        });
        return _flattened;
    }

    private _getContextData(webinarId: string) {
        const _hubspotTrackingCookie = this._formUtilsService.getHubspotTrackingCookie();
        const _currentPageUrl = this._formUtilsService.getCurrentUrl();
        const _currentPageName = this._formUtilsService.getCurrentPageName();

        let data = {
            hutk: _hubspotTrackingCookie,
            ipAddress: this._ip,
            pageUri: _currentPageUrl,
            pageName: _currentPageName
        };

        if (webinarId && webinarId !== '') {
            data['goToWebinarWebinarKey'] = webinarId;
        }
        return data;
    }

    private _getValueByField(field: any, setPrepopData: boolean): string {
        let _value = null;
        if (this._prepopData && this._prepopData.length > 0 && setPrepopData) {
            const _matchingValue = this._prepopData.filter((prepopData) => prepopData.name.toLowerCase() === field.name.toLowerCase());
            if (FormsUtils.isCheckboxes(field)) {
                _value = _matchingValue;
            } else {
                if (_matchingValue[0]) {
                    _value = _matchingValue[0].value;
                }
            }
        }
        return _value;
    }
}
